<template>
  <div class="grid gap-6 md:grid-cols-2">
    <article
      class="flex flex-col rounded-2xl border bg-white p-6 shadow-sm"
      :class="highlight === 'monthly' ? 'border-primary-500 ring-2 ring-primary-100' : 'border-slate-200'"
    >
      <p class="text-sm font-medium text-primary-600">Lunar</p>
      <p class="mt-2 text-4xl font-semibold tracking-tight">
        {{ monthlyLabel }}
      </p>
      <p class="mt-1 text-sm text-slate-500">pe lună</p>
      <ul class="mt-6 flex-1 space-y-2 text-sm text-slate-600">
        <li>Magazin complet</li>
        <li>{{ trialDays }} zile trial înainte de prima încasare        </li>
      </ul>
      <div v-if="addons.length" class="mt-5 space-y-3 border-t border-slate-100 pt-5">
        <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">Module extra</p>
        <div v-for="addon in addons" :key="addon.key" class="text-sm">
          <p class="font-medium text-slate-800">
            {{ addon.label }}
            <span class="font-normal text-slate-500">· {{ addonPrice(addon, 'monthly') }}</span>
          </p>
          <p class="mt-1 leading-5 text-slate-600">{{ addonCopy(addon) }}</p>
        </div>
      </div>
      <UButton
        class="mt-6"
        :to="monthlyEnabled ? '/start?plan=monthly' : undefined"
        :disabled="!monthlyEnabled"
        :label="monthlyEnabled ? 'Alege lunar' : 'Contactează-ne'"
        block
      />
    </article>

    <article
      class="flex flex-col rounded-2xl border bg-white p-6 shadow-sm"
      :class="highlight === 'yearly' ? 'border-primary-500 ring-2 ring-primary-100' : 'border-slate-200'"
    >
      <p class="text-sm font-medium text-primary-600">Anual</p>
      <p class="mt-2 text-4xl font-semibold tracking-tight">
        {{ yearlyLabel }}
      </p>
      <p class="mt-1 text-sm text-slate-500">pe an</p>
      <p v-if="yearlySavingsPercent > 0" class="mt-2 text-sm font-medium text-primary-700">
        cu {{ yearlySavingsPercent }}% mai ieftin decât 12 luni plătite lunar
      </p>
      <ul class="mt-6 flex-1 space-y-2 text-sm text-slate-600">
        <li>{{ trialDays }} zile trial înainte de prima încasare</li>
      </ul>
      <div v-if="addons.length" class="mt-5 space-y-3 border-t border-slate-100 pt-5">
        <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">Module extra</p>
        <div v-for="addon in addons" :key="`y-${addon.key}`" class="text-sm">
          <p class="font-medium text-slate-800">
            {{ addon.label }}
            <span class="font-normal text-slate-500">· {{ addonPrice(addon, 'yearly') }}</span>
          </p>
          <p class="mt-1 leading-5 text-slate-600">{{ addonCopy(addon) }}</p>
        </div>
      </div>
      <UButton
        class="mt-6"
        :to="yearlyEnabled ? '/start?plan=yearly' : undefined"
        :disabled="!yearlyEnabled"
        :label="yearlyEnabled ? 'Alege anual' : 'Contactează-ne'"
        block
      />
    </article>
  </div>
</template>

<script setup lang="ts">
import type { PlatformPublicPlans } from '~/composables/usePlatformPlans'

const props = withDefaults(defineProps<{
  plans: PlatformPublicPlans | null
  highlight?: 'monthly' | 'yearly' | null
}>(), {
  highlight: null,
})

const { formatMoney } = usePlatformPlans()

const trialDays = computed(() => props.plans?.trialDays || 14)
const monthlyEnabled = computed(() => Boolean(props.plans?.signupEnabled && (props.plans?.monthlyAmount || 0) > 0))
const yearlyEnabled = computed(() => Boolean(props.plans?.signupEnabled && (props.plans?.yearlyAmount || 0) > 0))

const monthlyLabel = computed(() => {
  if (!props.plans) return '—'
  if ((props.plans.monthlyAmount || 0) <= 0) return 'Contactează-ne'
  return formatMoney(props.plans.monthlyAmount, props.plans.currency)
})

const yearlyLabel = computed(() => {
  if (!props.plans) return '—'
  if ((props.plans.yearlyAmount || 0) <= 0) return 'Contactează-ne'
  return formatMoney(props.plans.yearlyAmount, props.plans.currency)
})

const addons = computed(() => props.plans?.addons || [])

const yearlySavingsPercent = computed(() => {
  const monthly = Number(props.plans?.monthlyAmount) || 0
  const yearly = Number(props.plans?.yearlyAmount) || 0
  if (monthly <= 0 || yearly <= 0) return 0
  const fullYear = monthly * 12
  if (yearly >= fullYear) return 0
  return Math.round(((fullYear - yearly) / fullYear) * 100)
})

const addonPublicCopy: Record<string, string> = {
  sell: 'Clienții își evaluează produsul și ți-l vând ție. Trimit o cerere, iar tu o vezi în admin, cu prețul calculat după regulile tale.',
  returns: 'Clienții cer returul unei comenzi. Tu aprobi cererea, generezi AWB de retur și urmărești statusul până la primire.',
  offers: 'Alegi produsele și reducerea. Prețul promoțional se actualizează singur, iar ofertele pot apărea pe prima pagină și pe /oferte.',
  generators: 'Clientul își configurează produsul și vede prețul pe loc. La continuare se creează produsul și intră în coș.',
}

function addonCopy(addon: { key: string, description?: string }) {
  return addonPublicCopy[addon.key] || addon.description || ''
}

function addonPrice(
  addon: { monthlyAmount: number, yearlyAmount: number, setupAmount?: number },
  interval: 'monthly' | 'yearly',
) {
  const currency = props.plans?.currency
  const setup = addon.setupAmount && addon.setupAmount > 0
    ? `setup ${formatMoney(addon.setupAmount, currency)} o singură dată`
    : ''
  const recurring = interval === 'yearly'
    ? (addon.yearlyAmount > 0 ? addon.yearlyAmount : addon.monthlyAmount * 12)
    : addon.monthlyAmount
  if (recurring <= 0) return setup ? `+ ${setup}` : 'inclus, se activează din Abonamentul meu'
  const label = `+ ${formatMoney(recurring, currency)} / ${interval === 'yearly' ? 'an' : 'lună'}`
  return setup ? `${label} · ${setup}` : label
}
</script>
